import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

/**
 * Custom hook to fetch settlements filtered by Group, Year, and Month via WebSocket.
 * Synchronizes with the backend using numeric month/year values.
 */
export default function useSettlementsByGroupId(selectedYear, selectedMonth) {
    const [settlements, setSettlements] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const { send, isConnected, connect, registerHandler } = useWebSocketContext();

    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const groupId = localStorage.getItem("group_id");

    const fetchSettlements = useCallback(
        async (attempt = 0) => {
            if (!mountedRef.current || !groupId) return;

            setIsLoading(true);
            setError("");

            try {
                if (!isConnected) {
                    try {
                        await connect();
                    } catch {
                        throw new Error("WebSocket not connected");
                    }
                }

                console.log(`Fetching settlements for: Month ${selectedMonth}, Year ${selectedYear}`);

                // Send year and month as Numbers to match Go's getIntValue (float64 case)
                const response = await send("cms_get_settlements", {
                    group_id: groupId,
                    year: Number(selectedYear),
                    month: Number(selectedMonth)
                });

                console.log("Fetched settlement by ID:", response);
                
                if (
                    !response ||
                    response.type !== "cms_get_settlements_response" ||
                    !Array.isArray(response.data)
                ) {
                    throw new Error("");
                }

                // Update state with whatever the server returned (empty or full)
                const data = response.data;
                setSettlements(data);

                // Manage cache based on whether data exists
                if (data.length > 0) {
                    localStorage.setItem("settlements_cache", JSON.stringify(data));
                } else {
                    localStorage.removeItem("settlements_cache");
                }
                
                setMessage(response.message || "Settlements loaded");
            } catch (err) {
                console.warn("Settlement fetch failed:", err.message);
                setError(err.message);

                // Exponential backoff retry logic
                
            } finally {
                if (mountedRef.current) setIsLoading(false);
            }
        },
        [groupId, isConnected, connect, send, selectedYear, selectedMonth]
    );

    useEffect(() => {
        mountedRef.current = true;

        // Fetch whenever Year or Month dependencies change
        fetchSettlements();

        // Register for real-time updates for this specific group
        const unregister = registerHandler(
            `settlements_update_group_${groupId}`,
            (msg) => {
                if (msg && Array.isArray(msg.data)) {
                    setSettlements(msg.data);
                    localStorage.setItem("settlements_cache", JSON.stringify(msg.data));
                }
            }
        );

        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            if (unregister) unregister();
        };
    }, [fetchSettlements, registerHandler, groupId]);

    return {
        settlements,
        isLoading,
        error,
        message,
        refetch: () => fetchSettlements(0),
    };
}